import type { ComponentProps } from 'react';
import { Box, styled } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useContextMenu } from '@/hooks/useContextMenu';
import { ApiErrorAlert } from '../Errors/ApiErrorAlert';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY, ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { ReportContextMenu } from './ReportContextMenu';
import { ReportDownload } from './ReportDownload';
import { FolderContent, JobStatus, UUID } from '@/api/generatedApi';
import { ReportZeroState } from './ReportZeroState';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useEffect, useState, useMemo } from 'react';
import { useTenants } from '@/services/tenants/useTenants';
import { useReportDrag } from './ReportDragContext';
import { matchPercentageColumn, reportTreeColumns } from './reportTableColumnDefinitions';
import { ReportGroupTable } from './ReportGroupTable';
import { useGetReportGroupFilters } from '@/hooks/api/reports/useGetReportGroupFilters';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { insertMetadataColumnsAfterField } from '@/utils/metadata';
import { useReportViewMode } from '@/hooks/useReportViewMode';
import { ReportViewModeToggle } from './ReportViewModeToggle';
import { ReportMatchListView } from './ReportMatchListView';
import { ReportMasterDetail } from './ReportMasterDetail';
import { useReportMasterDetail } from './ReportMasterDetailContext';
import { isLabelPending } from '@/utils/assets';

const getGroupDisplayName = (group: { name?: string } | undefined, index: number) => {
  if (group?.name) return group.name;
  return index === -1 ? '' : `Group ${index + 1}`;
};

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ReportTable = () => {
  const { id, groupId } = useParams();
  const { gridApiReference } = useReportDrag();
  const navigate = useNavigate();
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const { report } = useGetReport({ id });
  const {
    groups,
    isLoading,
    error: groupsError,
    rawResult: { refetch },
  } = useInfiniteReportGroups({ id });

  const [hasNavigated, setHasNavigated] = useState<boolean>(false);

  useEffect(() => {
    if (!groupId && !hasNavigated && !isLoading && groups.length > 0) {
      navigate(getPath(`report/${id}/${groups[0]?.id}`), { replace: true });
      setHasNavigated(true);
    }
  }, [groupId, hasNavigated, isLoading, groups, navigate, getPath, id]);

  const selectedGroupIndex = groups.findIndex(group => group.id === groupId);
  const selectedGroup = selectedGroupIndex === -1 ? undefined : groups[selectedGroupIndex];
  const assetIdsTotal = selectedGroup?.assetIds.length ?? 0;

  const { data: reportGroupFiltersData, isLoading: isLoadingReportGroupFilters } = useGetReportGroupFilters({
    reportId: id ?? '',
    groupId: groupId ?? '',
    enabled: !!id && !!groupId,
  });

  const metadataFieldNames = useMemo(
    () => Object.keys(reportGroupFiltersData?.filterData.metadata ?? {}),
    [reportGroupFiltersData?.filterData.metadata]
  );

  const canManageReportGroups = permissions.canManageReportGroups;

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<FolderContent>({
    canUseContextMenu: true,
    getRowById: (rowId: UUID): FolderContent | undefined => {
      const gridRow = gridApiReference.current?.getRow<ReportGroupAsset>(rowId);
      if (gridRow) return gridRow;
      const allRowIds = gridApiReference.current?.getAllRowIds() ?? [];
      for (const id of allRowIds) {
        const parentRow = gridApiReference.current?.getRow<ReportGroupAsset>(id);
        const match = parentRow?.matches?.find(m => m.asset.id === rowId);
        if (match) return { ...match.asset, contentType: 'asset' as const };
      }
      for (const asset of allAssets) {
        if (asset.id === rowId) return { ...asset, contentType: 'asset' as const };
        const match = asset.matches?.find(m => m.asset.id === rowId);
        if (match) return { ...match.asset, contentType: 'asset' as const };
      }
      return undefined;
    },
  });

  const [cardContextMenu, setCardContextMenu] = useState<{
    position: { left: number; top: number };
    item: FolderContent;
  }>();

  const handleCardContextMenu = (item: FolderContent, event: React.MouseEvent) => {
    handleCloseContextMenu();
    setCardContextMenu({ position: { left: event.clientX, top: event.clientY }, item });
  };

  const handleCloseCardContextMenu = () => {
    setCardContextMenu(undefined);
  };

  const activeContextMenuPosition = contextMenuPosition ?? cardContextMenu?.position;
  const activeContextMenuClickedItem = contextMenuClickedItem ?? cardContextMenu?.item;
  const handleCloseAnyContextMenu = () => {
    handleCloseContextMenu();
    handleCloseCardContextMenu();
  };

  const [viewMode, setViewMode] = useReportViewMode(id ?? '');

  const { hasMatchData, allAssets, allAssetsTotal, isLoadingAllAssets, allAssetsError } = useReportMasterDetail();

  const hasLabels = useMemo(() => allAssets.some(asset => asset.label?.summary || isLabelPending(asset)), [allAssets]);

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames: isLoadingReportGroupFilters,
      canEditMetadata: false,
      contextKey: `${id || ''}-${groupId || ''}`,
      hasLabels,
    });

  const assetsError = allAssetsError ? { message: allAssetsError.message } : undefined;

  const fetchRows = useMemo(() => {
    if (allAssets.length === 0) return;
    return async (parameters: { page: number; perPage: number }) => {
      const start = (parameters.page - 1) * parameters.perPage;
      return { assets: allAssets.slice(start, start + parameters.perPage), pageData: { total: allAssetsTotal } };
    };
  }, [allAssets, allAssetsTotal]);

  const baseColumns = hasMatchData
    ? [reportTreeColumns[0], matchPercentageColumn, ...reportTreeColumns.slice(1)]
    : reportTreeColumns;

  const columns = useMemo(
    () => insertMetadataColumnsAfterField(baseColumns, metadataColumns, 'folder'),
    [metadataColumns, hasMatchData]
  );

  const handleAssetRemoved = () => {
    refetch();
    queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
  };

  let content: React.ReactNode;

  if (!isLoading && assetsError?.message === 'Report group not found') {
    content = <ReportZeroState title='This group no longer exists' />;
  } else if (!isLoading && (groupsError || assetsError)) {
    content = <ApiErrorAlert error={groupsError || assetsError} />;
  } else if (!isLoading && groups.length === 0) {
    content = <ReportZeroState title='No Groups Found' description='This report has no groups.' />;
  } else if (!isLoading && selectedGroup && assetIdsTotal === 0) {
    content = (
      <ReportZeroState
        title='No Assets in Group'
        description='This group is empty. Drag-and-drop assets from other groups to add assets.'
      />
    );
  } else {
    const downloadButton = report?.status === JobStatus.COMPLETED && id ? <ReportDownload id={id} /> : undefined;
    const viewToggle =
      hasMatchData || viewMode === 'matches' ? (
        <ReportViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      ) : undefined;

    const groupName = getGroupDisplayName(selectedGroup, selectedGroupIndex);
    const toolbarActionButtons = (
      <>
        {viewToggle}
        {downloadButton}
      </>
    );

    let viewContent: React.ReactNode;
    if (viewMode === 'table' && hasMatchData) {
      viewContent = (
        <ReportMasterDetail
          assets={allAssets}
          groupName={groupName}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          toolbarActions={toolbarActionButtons}
          onRowContextMenu={canManageReportGroups ? handleGridContextMenu : undefined}
        />
      );
    } else if (viewMode === 'table') {
      viewContent = (
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', overflow: 'hidden' }}>
          <ReportGroupTable
            fetchRows={fetchRows}
            error={assetsError}
            isLoading={!groupId || isLoading || isMetadataLoading || isLoadingAllAssets}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            gridApiRef={gridApiReference}
            onRowContextMenu={canManageReportGroups ? handleGridContextMenu : undefined}
            toolbarActions={toolbarActionButtons}
            showMatchDetails={false}
          />
        </Box>
      );
    } else {
      viewContent = (
        <ReportMatchListView
          assets={allAssets}
          groupName={groupName}
          isLoading={isLoadingAllAssets}
          toolbarActions={toolbarActionButtons}
          onCardContextMenu={canManageReportGroups ? handleCardContextMenu : undefined}
        />
      );
    }

    content = (
      <>
        {viewContent}
        {id && selectedGroup && (
          <ReportContextMenu
            anchorPosition={activeContextMenuPosition}
            onClose={handleCloseAnyContextMenu}
            clickedItem={activeContextMenuClickedItem}
            clickedField={contextMenuClickedField}
            columns={columns as ComponentProps<typeof ReportContextMenu>['columns']}
            reportId={id}
            groupId={selectedGroup.id}
            onRemoved={handleAssetRemoved}
            assetCount={assetIdsTotal}
            canManageGroups={canManageReportGroups}
          />
        )}
      </>
    );
  }

  return <ContentArea data-testid={ReportPageObjectModel.ReportTable}>{content}</ContentArea>;
};
